import React from "react";
import { Helmet } from "react-helmet-async";

export default function Mission() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-20">
      {/* ============================
          SEO
      ============================ */}
      <Helmet>
        <title>Mission | Aerzen Air™ Clean Indoor Air Deployment</title>
        <meta
          name="description"
          content="Aerzen Air™ mission focuses on responsible deployment of reliable indoor air purification systems across India with long-term performance and serviceability."
        />
        <link
          rel="canonical"
          href="https://www.aerzenair.com/mission"
        />
      </Helmet>

      {/* ======================================================
          TITLE
      ====================================================== */}
      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Mission
        </h1>

        <p className="text-gray-700 max-w-3xl">
          A measured, accountable approach to deploying clean indoor air
          solutions across India.
        </p>
      </div>

      {/* ======================================================
          MAIN GRID
      ====================================================== */}
      <div className="grid md:grid-cols-2 gap-12 items-start">

        {/* LEFT — CONTENT */}
        <div>
          <p className="text-gray-700 mb-4">
            The mission of <strong>AERZEN AIR™</strong> is to deploy reliable,
            application-appropriate air purification appliances across Indian
            households and shared indoor environments, with a focus on practical
            performance rather than theoretical capability.
          </p>

          <p className="text-gray-700 mb-6">
            Deployment decisions are guided by real indoor usage patterns,
            regional pollution profiles, power availability, maintenance
            realities, and long-term ownership considerations — ensuring that
            solutions remain effective beyond initial installation.
          </p>

          <h2 className="text-xl font-semibold mb-3">
            Measurable Objective
          </h2>

          <p className="text-gray-700 mb-4">
            Reach <strong>1,00,000 deployed units by 2028</strong>, through
            responsible scaling, controlled supply expansion, and consistent
            post-deployment support across residential, institutional, and
            shared indoor environments.
          </p>

          <p className="text-gray-700 mb-6">
            This target reflects a balance between reach and responsibility —
            ensuring that growth does not compromise service quality,
            performance integrity, or long-term sustainability.
          </p>

          <p className="text-gray-700 mb-6">
            The mission prioritizes outcomes over output — focusing on sustained
            air quality improvement, informed usage, and system longevity rather
            than short-term adoption metrics or volume-first expansion.
          </p>

          {/* OPERATING PRINCIPLES */}
          <div className="bg-gray-50 border rounded-lg p-6">
            <h3 className="font-semibold mb-4">
              Operating Principles
            </h3>

            <ul className="text-gray-700 space-y-2 text-sm">
              <li>• Design specifically for Indian indoor air quality conditions</li>
              <li>• Prioritize long-term reliability over feature excess</li>
              <li>• Maintain transparency in performance representation</li>
              <li>• Build systems that can be serviced, sustained, and supported</li>
            </ul>
          </div>
        </div>

        {/* RIGHT — IMAGE */}
        <div className="rounded-xl bg-gray-100 p-6 flex items-center justify-center">
          <img
            src="/images/mission/mission-hero.jpg"
            alt="Mission-driven deployment of indoor air purification systems"
            className="w-full max-w-[520px] object-contain rounded-lg"
          />
        </div>
      </div>

      {/* ======================================================
          CLOSING LINE
      ====================================================== */}
      <div className="mt-12 max-w-4xl">
        <p className="text-gray-700">
          The mission emphasizes controlled deployment, real-world usability,
          and long-term serviceability — ensuring that adoption remains
          sustainable, verifiable, and meaningful for households, institutions,
          and public stakeholders alike.
        </p>
      </div>
    </section>
  );
}
